import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import { KeyboardArrowDown as ArrowIcon } from '@mui/icons-material';
import 'flag-icons/css/flag-icons.min.css';

const languages = [
    { code: 'en', name: 'English', label: 'English', flagClass: 'fi fi-us' },
    { code: 'fr', name: 'Français', label: 'Français', flagClass: 'fi fi-fr' },
    { code: 'ar', name: 'العربية', label: 'العربية', flagClass: 'fi fi-tn' },
];

const LanguageSwitcher = () => {
    const { i18n } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLanguageChange = (langCode: string) => {
        i18n.changeLanguage(langCode);
        document.documentElement.dir = 'ltr';
        document.documentElement.lang = langCode;
        handleClose();
    };

    const currentLanguage = languages.find(l => l.code === i18n.language) || languages[0];

    return (
        <Box>
            <Button
                id="language-button"
                aria-controls={open ? 'language-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                endIcon={<ArrowIcon sx={{
                    transition: 'transform 0.2s',
                    transform: open ? 'rotate(180deg)' : 'none',
                    fontSize: '1rem',
                    color: '#6B7280'
                }} />}
                sx={{
                    bgcolor: '#F3F4F6',
                    borderRadius: '50px',
                    px: 1.5,
                    py: 0.6,
                    minHeight: 36,
                    textTransform: 'none',
                    color: '#111827',
                    fontWeight: 500,
                    border: '1px solid #E5E7EB',
                    '&:hover': {
                        bgcolor: '#E5E7EB',
                        border: '1px solid #D1D5DB',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    transition: 'all 0.2s ease',
                }}
            >
                <span className={currentLanguage.flagClass} style={{ fontSize: '1rem', borderRadius: '2px' }}></span>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    {currentLanguage.label}
                </Typography>
            </Button>

            <Menu
                id="language-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'language-button',
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        mt: 1,
                        minWidth: 160,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: '1px solid #E5E7EB',
                        p: 0.5,
                    }
                }}
            >
                {languages.map((language) => (
                    <MenuItem
                        key={language.code}
                        onClick={() => handleLanguageChange(language.code)}
                        selected={i18n.language === language.code}
                        sx={{
                            borderRadius: '8px',
                            py: 1,
                            px: 1.5,
                            gap: 1.5,
                            '&.Mui-selected': {
                                bgcolor: '#F3F4F6',
                                fontWeight: 600,
                                '&:hover': {
                                    bgcolor: '#E5E7EB',
                                }
                            }
                        }}
                    >
                        <span className={language.flagClass} style={{ fontSize: '1.1rem', borderRadius: '2px' }}></span>
                        <Typography variant="body2" sx={{ fontWeight: i18n.language === language.code ? 600 : 500 }}>
                            {language.name}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default LanguageSwitcher;
